import React from 'react';
import { FiCheck, FiArrowRight } from 'react-icons/fi';
import { LuRefrigerator, LuIceCreamCone } from 'react-icons/lu'; // Fixed LuIceCream to LuIceCreamCone
import { FaSnowflake } from 'react-icons/fa';
import { FiList, FiDownload, FiBell } from 'react-icons/fi';
import { LuTrendingUp } from 'react-icons/lu';
import { Link } from 'react-router-dom';

const TemperatureStatusOverview = () => {
  const sections = [
    {
      title: 'Chillers',
      status: 'All Good',
      avgValue: '2.4°C',
      idealRange: 'Ideal: 0°C to 5°C',
      icon: LuRefrigerator, 
      hasSnowflake: true,
      lastUpdate: '1 min ago',
      equipments: [
        { name: 'Chiller 1', value: '1.9°C' },
        { name: 'Chiller 2', value: '2.4°C' },
        { name: 'Prep Chill', value: '2.1°C' },
        { name: 'Drinks Fridge', value: '3.2°C' },
      ]
    },
    {
      title: 'Refrigerators',
      status: 'All Good',
      avgValue: '3.1°C',
      idealRange: 'Ideal: 0°C to 5°C',
      icon: LuRefrigerator,
      hasSnowflake: false,
      lastUpdate: '1 min ago',
      equipments: [
        { name: 'Fridge Main', value: '3.1°C' },
        { name: 'Fridge Prep', value: '2.8°C' },
        { name: 'Fridge Underbar', value: '3.3°C' },
      ]
    },
    {
      title: 'Freezers',
      status: 'All Good',
      avgValue: '-18.6°C',
      idealRange: 'Ideal: -18°C or below',
      icon: LuIceCreamCone, // Using the verified Lucide icon name
      hasSnowflake: true,
      lastUpdate: '1 min ago',
      equipments: [
        { name: 'Freezer 1', value: '-18.6°C' },
        { name: 'Freezer 2', value: '-19.1°C' },
        { name: 'Ice Cream Freezer', value: '-20.2°C' },
      ]
    }
  ];

  const actions = [
    { label: 'View All Sensors', icon: FiList , link : '/categories'},
    { label: 'Manage Alerts', icon: FiBell , link : '/#'},
  ];


  return (
    <section className="w-full bg-white p-6 rounded-2xl border border-gray-100 shadow-sm font-sans">
      
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[17px] font-bold text-gray-900 tracking-tight">
          Temperature Status Overview
        </h2>
        <div className="flex items-center gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {actions.map((action, idx) => {
              const Icon = action.icon;
              return (
                <Link key={idx} to={action.link}>
                  <button
                    className="flex items-center justify-center gap-3 bg-white border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 active:bg-gray-50 p-4 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.01)] transition-all group w-full"
                  >
                    {/* Icon matching the exact dashboard blue brand color */}
                    <Icon className="w-[18px] h-[18px] text-[#0052FF] shrink-0 stroke-[2.2]" />
                    
                    {/* Button Label */}
                    <span className="text-[14px] font-bold text-[#0052FF] tracking-tight whitespace-nowrap">
                      {action.label}
                    </span>
                  </button>
                </Link>
              );
            })}
          </div>
          <Link to="/equipment" className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:opacity-80 transition-opacity">
            <span>View all equipment</span>
            <FiArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
          </Link>
        </div>
      </div>

      {/* Main Responsive Grid Wrapper */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {sections.map((section, idx) => {
          const ApplianceIcon = section.icon;
          return (
            <div 
              key={idx} 
              className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col justify-between"
            >
              <div>
                {/* Card Title & Icon Header Row */}
                <div className="flex items-start justify-between">
                  <div className="relative flex items-center">
                    <div className="text-blue-600 p-1">
                      <ApplianceIcon className="w-11 h-11 stroke-[1.5]" />
                    </div>
                    {section.hasSnowflake && (
                      <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5 shadow-sm text-blue-500">
                        <FaSnowflake className="w-4 h-4" />
                      </div>
                    )}
                    <div className="ml-3">
                      <h3 className="text-[16px] font-bold text-gray-900 leading-tight">
                        {section.title}
                      </h3>
                      <span className="inline-block mt-1 text-[11px] font-bold px-2 py-0.5 text-emerald-600 bg-emerald-50 rounded-full">
                        {section.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Big Metric Readout */}
                <div className="mt-5 text-center">
                  <p className="text-[34px] font-bold text-emerald-600 tracking-tight leading-none">
                    {section.avgValue}
                  </p>
                  <span className="text-[12px] font-medium text-gray-500 block mt-2">
                    {section.idealRange}
                  </span>
                </div>

                {/* Sub-equipment Row Layout */}
                <div className="mt-8 flex items-center justify-start gap-5 overflow-x-auto pb-1 scrollbar-hide">
                  {section.equipments.map((equip, eqIdx) => (
                    <div key={eqIdx} className="flex flex-col items-center text-center min-w-[65px] flex-1">
                      <div className="w-[18px] h-[18px] bg-white border border-emerald-500 text-emerald-600 rounded-full flex items-center justify-center mb-2 shadow-sm">
                        <FiCheck className="w-3 h-3 stroke-[2.5]" />
                      </div>
                      <p className="text-[11px] font-medium text-gray-500 truncate w-full mb-0.5">
                        {equip.name}
                      </p>
                      <p className="text-[13px] font-bold text-gray-900 whitespace-nowrap">
                        {equip.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Update Footnote Indicator */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-1.5 text-[12px] font-medium text-gray-400">
                <span className="w-2 h-2 bg-emerald-500 rounded-full inline-block animate-pulse"></span>
                <span>Last updated: {section.lastUpdate}</span>
              </div>

            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TemperatureStatusOverview;
